import { Modal } from "bootstrap";

/**
  * Send the form data to the server
  * @param {HTMLElement} form The form to submit
  * @return {Object} Returns an object containing a success or error property, 
  * or a Javascript error object
  */
function submitFormData(formData) {
	return new Promise((resolve, reject) => {
		try {
			fetch(params.ajax_url, { method: 'post', body: formData })
			.then(res => res.json())
			.then(data => resolve(data));
		}
		catch (err) {
			reject(err);
		}
	});
}

/**
  * Display field and form errors
  * @param {Object} errors The errors object from the server side form validation
  * @param {HTMLElement} form The form which contains the fields with errors
  * @param {HTMLElement} feedbackElement Pass in a element to display feedback messages.
  * @param {HTMLElement} feedbackModal Pass in a modal to display feedback messages.
  */
function showFormFeedback(successOrErrors, form, feedbackElement = null, feedbackModal = null) {
	if (successOrErrors.success) {
		// Handle success
		if (feedbackElement) {
			showFeedbackInForm(feedbackElement, successOrErrors.success, false);
		} else if (feedbackModal) {
			showFeedbackInModal(feedbackModal, successOrErrors.success, false);
		} 
	} else {
		let errorsArray = [];
		// Handle errors
		Object.entries(successOrErrors.errors).forEach(([fieldName, error]) => {
			// General form error
			if (fieldName === 'dummy') {
			// Show form feedback in an element
			if (feedbackElement) {          
				showFeedbackInForm(feedbackElement, successOrErrors.errors.dummy, true);
			// Show form feedback in a bootstrap modal
			} else if (feedbackModal) {
				showFeedbackInModal(feedbackModal, successOrErrors.errors.dummy, true);
			} 
			} else {
			// Add error message to invalid checkboxes (name="consent")
			if (form.querySelector(".field-wrapper [name^='" + fieldName + "'][type='radio']")) {
				const radioInputs = form.querySelectorAll(".field-wrapper [name^='" + fieldName + "']");
				const firstRadio = radioInputs[0];
				const fieldWrapper = firstRadio.closest(".field-wrapper");
				
				// Mark all radio inputs as invalid
				radioInputs.forEach(function (el) {
					el.classList.add("is-invalid");
					el.setAttribute("aria-invalid", true);
				});
				
				// Add is-invalid to field-wrapper and append error message as direct child
				if (fieldWrapper) {
					fieldWrapper.classList.add("is-invalid");
					const feedbackElem = document.createElement("p");
					feedbackElem.classList.add("invalid-feedback");
					feedbackElem.id = firstRadio.name + "-validation";
					feedbackElem.innerHTML = error;
					fieldWrapper.appendChild(feedbackElem);
				}
				
				// Set aria-describedby on all radio inputs
				radioInputs.forEach(function (el) {
					el.setAttribute("aria-describedby", firstRadio.name + "-validation");
				});
				
				errorsArray = [...errorsArray, error];
			// Add error message to phone field with input-group
			} else if (form.querySelector(".input-group.cpi-input [name^='" + fieldName + "']")) {
				const input = form.querySelector(".input-group.cpi-input [name^='" + fieldName + "']");
				const inputGroup = input.closest(".input-group");
				const fieldWrapper = input.closest(".field-wrapper");
				
				input.classList.add("is-invalid");
				input.setAttribute("aria-describedby", input.id + "-validation");
				input.setAttribute("aria-invalid", true);
				inputGroup.classList.add("is-invalid");
				
				if (fieldWrapper) {
					fieldWrapper.classList.add("is-invalid");
					const feedbackElem = document.createElement("p");
					feedbackElem.classList.add("invalid-feedback");
					feedbackElem.id = input.id + "-validation";
					feedbackElem.innerHTML = error;
					fieldWrapper.appendChild(feedbackElem);
				}
				
				errorsArray = [...errorsArray, error];
			} else {
			// Add error message to other invalid inputs
				form.querySelectorAll(".field-wrapper [name^='" + fieldName + "']").forEach(function (el) {
					const fieldWrapper = el.closest(".field-wrapper");
					el.classList.add("is-invalid");
					el.setAttribute("aria-describedby", el.id + "-validation");
					el.setAttribute("aria-invalid", true);
					if (fieldWrapper) {
						fieldWrapper.classList.add("is-invalid");
						const feedbackElem = document.createElement("p");
						feedbackElem.classList.add("invalid-feedback");
						feedbackElem.id = el.id + "-validation";
						feedbackElem.innerHTML = error;
						fieldWrapper.appendChild(feedbackElem);
					}
					errorsArray = [...errorsArray, error];
				});
			}
			}
		});
		if(errorsArray.length > 0){
			if (feedbackElement) {
				showFeedbackInForm(feedbackElement, 'There was a problem submitting the form. Please correct the errors shown and try again.', true);
			} else if (feedbackModal) {
				showFeedbackInModal(feedbackModal, 'There was a problem submitting the form. Please correct the errors shown and try again.', true);
			} 	
		}
	}
}

/**
  * Add form feedback to a feedback element
  * @param {HTMLElement} feedbackElement The element to display any feeback in
  * @param {String} feedback The content of the feedback
  * * @param {Boolean} isError If the feeback is an error message
  * 
  */
function showFeedbackInForm(feedbackElement, feedback, isError) {
	if(isError) {
		feedbackElement.classList.add('is-invalid');
		feedbackElement.parentElement.classList.add('invalid-feedback');
	}
	feedbackElement.innerHTML = feedback;
	feedbackElement.style.display = 'block';
}


/**
  * Add form feedback to a Bootstrap modal and show the modal
  * @param {HTMLElement} feedbackModal The Bootstrap modal
  * @param {String} feedback The content of the feedback
  * @param {Boolean} isError If the feeback is an error message
  * 
  */
function showFeedbackInModal(feedbackModal, feedback, isError) {
	const modal = Modal.getOrCreateInstance(feedbackModal);
	const feedbackHeading = feedbackModal.querySelector(".modal-title");
	const feedbackContainer = feedbackModal.querySelector('.modal-body');
	const closeModalBtn = feedbackModal.querySelector(".close-modal")
	if(isError) {
		feedbackHeading.textContent = "Form submission error"
		feedbackContainer.classList.add('is-invalid');
		feedbackContainer.parentElement.classList.add('invalid-feedback');
	} else {
		feedbackHeading.textContent = "Success";
		feedbackContainer.classList.remove('is-invalid');
		feedbackContainer.parentElement.classList.remove('invalid-feedback');
	}
	feedbackContainer.innerHTML = feedback;
	modal.show();  
}


/**
  * Add form feedback to a feedback element
  * @param {HTMLElement} feedbackElement The element to display any feeback in
  * @param {String} feedback The content of the feedback
  * * @param {Boolean} isError If the feeback is an error message
  * 
  */
function insertErrorFeedback(form, errorsArray) {
	const errors = errorsArray.map(error => `<li tabindex="0">${error}</li>`).join('');
	const errorContainer = `<div class='form-errors'><h2 class='form-errors-heading' tabindex="0">Form not submitted. Please correct the following errors</h2><ul>${errors}</ul></div>`;
	form.insertAdjacentHTML('afterbegin', errorContainer);
	const errorsHeading = document.querySelector(".form-errors-heading");
	errorsHeading.focus();  
}


/**
  * Validate the privacy policy checkbox, which is not nested in the form
  * @param {HTMLElement} checkbox The privacy policy checkbox element
  * 
  */
function validateCheckbox(checkbox) {
	if(!checkbox.checked){
		// Trigger the browser validation if the privacy checkbox is not checked
		checkbox.setCustomValidity('Please confirm your agreement.');
		checkbox.reportValidity();
		return false;
	} else {
		// Clear any previous validation message
		checkbox.setCustomValidity('')
		return true;    
	}
}

export {submitFormData, showFormFeedback, showFeedbackInModal, showFeedbackInForm, validateCheckbox};

